/**
 * Centralized formatting helpers. All user-visible dates / numbers MUST go
 * through this module so we get consistent locale handling and can redact /
 * fallback predictably.
 *
 * Do NOT concatenate locale-specific strings in components. If you need a new
 * format, add it here.
 */

const DEFAULT_LOCALE = 'en-CA';

export type DateInput = Date | string | number | null | undefined;
export type NumberInput = number | null | undefined;

function coerceDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function formatDate(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: '2-digit' },
  locale: string = DEFAULT_LOCALE,
): string {
  // Date-only strings (`YYYY-MM-DD`) have no time component and no tz.
  // JS `new Date('2026-06-02')` parses them as UTC midnight, then
  // `Intl.DateTimeFormat` formats in the browser's local tz — which
  // shifts the calendar day for anyone west of UTC. Format these in
  // UTC so we always render the literal calendar date the user typed.
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map((p) => Number.parseInt(p, 10));
    const date = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1));
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(date);
  }
  const date = coerceDate(value);
  if (date === null) return '—';
  return new Intl.DateTimeFormat(locale, options).format(date);
}

export function formatDateTime(value: DateInput, locale: string = DEFAULT_LOCALE): string {
  return formatDate(
    value,
    { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    locale,
  );
}

/**
 * Returns the age in completed years from a date of birth. Returns ``null``
 * for unparseable / future dates so callers can omit the chip rather than
 * render "0y" or a negative number.
 *
 * Accepts ``YYYY-MM-DD``, ``YYYY-MM``, ``YYYY``, or any other parseable
 * ``DateInput``. Partial dates (year-only / year-month) widen the age by
 * one year of uncertainty; we still return the conservative completed-years
 * count.
 */
export function getAgeYears(value: DateInput, now: Date = new Date()): number | null {
  const date = coerceDate(value);
  if (date === null) return null;
  if (date.getTime() > now.getTime()) return null;
  let age = now.getFullYear() - date.getFullYear();
  const monthDelta = now.getMonth() - date.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }
  return age < 0 ? null : age;
}

export function formatNumber(
  value: NumberInput,
  options: Intl.NumberFormatOptions = {},
  locale: string = DEFAULT_LOCALE,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, options).format(value);
}

const PARTIAL_YEAR = /^\d{4}$/;
const PARTIAL_YEAR_MONTH = /^\d{4}-\d{2}$/;
const PARTIAL_FULL = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True only for a complete `YYYY-MM-DD` date. `DateInput` emits partial values
 * (`YYYY`, `YYYY-MM`) as the user types, which callers must not persist to a
 * wire field typed `format: date` — the backend rejects the whole payload.
 */
export function isFullIsoDate(value: string | null | undefined): boolean {
  return typeof value === 'string' && PARTIAL_FULL.test(value);
}

/**
 * Formats a partial ISO date string (YYYY, YYYY-MM, or YYYY-MM-DD) showing
 * only the precision available. Uses UTC to prevent timezone roll-back (e.g.
 * "1111" → "1110" in UTC-5).
 */
export function formatPartialDate(
  value: string | null | undefined,
  locale: string = DEFAULT_LOCALE,
): string {
  if (!value) return '—';
  if (PARTIAL_YEAR.test(value)) return value;
  if (PARTIAL_YEAR_MONTH.test(value)) {
    const parts = value.split('-');
    const d = new Date(Date.UTC(parseInt(parts[0] ?? '0', 10), parseInt(parts[1] ?? '1', 10) - 1, 1));
    // Get just the month name, strip any trailing locale period (e.g. "Apr." → "Apr"), then add ", YYYY"
    const monthName = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(d).replace(/\.$/, '');
    return `${monthName}, ${parts[0]}`;
  }
  if (PARTIAL_FULL.test(value)) {
    const parts = value.split('-');
    const d = new Date(Date.UTC(parseInt(parts[0] ?? '0', 10), parseInt(parts[1] ?? '1', 10) - 1, parseInt(parts[2] ?? '1', 10)));
    return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: '2-digit', timeZone: 'UTC' }).format(d);
  }
  return value;
}

/**
 * Address parts, in the order they should appear in a one-line display.
 * Any entry that is ``null`` / empty is skipped. Returns the empty-value
 * placeholder when every part is missing so UI code never has to branch.
 */
export interface AddressParts {
  readonly line: string | null;
  readonly city: string | null;
  readonly province: string | null;
  readonly postalCode: string | null;
  readonly country: string | null;
}

export function formatAddress(address: AddressParts, emptyValue = '—'): string {
  const parts = [
    address.line,
    address.city,
    address.province,
    address.postalCode,
    address.country,
  ].filter((part): part is string => part !== null && part.length > 0);
  return parts.length > 0 ? parts.join(', ') : emptyValue;
}
